// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DatabaseSetupDataFetcher.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import fetch from 'share/fetch/Izenda.Fetch';
import uiState from 'logic/Izenda.UiState';
import { isEmpty, isNil, isArray } from 'lodash/fp';
const ROOT_URL = 'DataModel/';
const FUSION_URL = 'fusion/';

export default {
  /**
   * Load query source's columns
   * @param {Object} querySourceDetails
   * @param {Object} sortInfo
   * @param {Object} pageInfo
   * @param {String} tenantId
   * @returns {Object}
   */
  loadQuerySourceFields(querySourceDetails: any, sortInfo: any, pageInfo: any, tenantId: string): Promise {
    pageInfo = pageInfo || {};

    const requestData = {
      querySource: querySourceDetails,
      criteria: [],
      tenantId,
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: 1000,
      sortOrders: sortInfo
    };

    return fetch(`${ROOT_URL}LoadQuerySourceFields`).post(requestData);
  },

  /**
   * Save Query Source
   * @returns {Object}
   */
  updateQuerySource(data: any): Promise {
    return fetch(ROOT_URL).post(data);
  },

  /**
   * Load query sources of query source type
   * @param {String} tenantId
   * @param {String} querySourceType
   * @param {Object} pageInfo
   * @param {Object} sortInfo
   * @param {Object} criteria
   * @returns {Object}
   */
  loadQuerySources(tenantId: string, querySourceType: string, pageInfo: any, sortInfo: any, criteria: any): Promise {
    pageInfo = pageInfo || {};

    const requestData = {
      querySourceType,
      tenantId,
      criteria: criteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: sortInfo || [{ key: 'name', descending: true }]
    };

    return fetch(`${ROOT_URL}LoadQuerySources`).post(requestData);
  },

  /**
   * Invoke a stored procedure
   * @param {String} querySourceId
   * @param {Array} postedParameter
   * @param {Object} sortInfo
   * @returns {Object}
   */
  executeStoredProc(querySourceId: string, postedParameter: any, sortInfo: any): Promise {
    const requestData = {
      querySourceId,
      postedParameters: postedParameter || [],
      sortOrders: sortInfo
    };

    return fetch(`${ROOT_URL}ReloadQuerySourceFields`).post(requestData);
  },

  /**
   * Get license schemas
   * @param {String} tenantId
   * @returns {Promise}
   */
  getSchemaDetails(tenantId: string): Promise {
    const tenant = isEmpty(tenantId) ? '' : tenantId;
    return fetch(`${ROOT_URL}Schema/${tenant}`).get();
  },

  /**
   * Get Join Types
   * @returns {Promise}
   */
  getJoinTypes(): Promise {
    return fetch(`${ROOT_URL}/relationship/joinTypes/`).get();
  },

  /**
   * Get list of Databases with basic info
   * @param {String} tenantId
   * @returns {Promise}
   */
  getBasicConnectionsInfo(tenantId: string): Promise {
    const tenant = isEmpty(tenantId) ? '' : tenantId;
    return fetch(`${ROOT_URL}basicConnectionsInfo/${tenant}`).get();
  },

  /**
   * Get list of table with basic info
   * @param {String} connectionId
   * @returns {Promise}
   */
  getBasicQuerySourcesInfo(connectionId: string): Promise {
    return fetch(`${ROOT_URL}basicQuerySourcesInfo/${connectionId}`).get();
  },

  /**
   * Get list of table with detail info
   * @param {String} connectionId
   * @returns {Promise}
   */
  getQuerySourcesInfo(connectionId: string): Promise {
    return fetch(`${ROOT_URL}querySourcesInfo/${connectionId}`).get();
  },

  /**
   * Get list of field with basic info
   * @param {String} querySourceId
   * @returns {Promise}
   */
  getBasicQuerySourceFieldsInfo(querySourceId: string): Promise {
    return fetch(`${ROOT_URL}basicQuerySourceFieldsInfo/${querySourceId}?type=1`).get();
  },

  /**
   * Get relationship list
   * @param {String} querySourceId
   * @param {String} tenantId
   * @param {Object} pageInfo
   * @param {Array} sortInfo
   * @param {Array} criteria
   * @returns {Promise}
   */
  getRelationships(querySourceId: string, tenantId: string, pageInfo: any, sortInfo: any, criteria: any): Promise {
    pageInfo = pageInfo || {};

    const requestData = {
      querySourceId,
      tenantId,
      criteria: criteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: sortInfo || [{ key: 'DataObject', descending: true }]
    };

    return fetch(`${ROOT_URL}loadRelationships/`).post(requestData);
  },

  /**
   * Get Invalid Relationships
   * @param {String} querySourceId
   * @param {String} tenantId
   * @param {Array} criteria
   * @param {Array} modifiedRelationships
   * @returns {Promise}
   */
  getInvalidRelationships(querySourceId: string, tenantId: string, sortInfo: any, criteria: any, modifiedRelationships: any): Promise {
    const requestData = {
      querySourceId,
      tenantId,
      criteria: criteria || [],
      sortOrders: sortInfo,
      modifiedRelationships
    };

    return fetch(`${ROOT_URL}loadInvalidRelationships/`).post(requestData);
  },

  /**
   * Validate Relationships
   * @param {Object} data
   * @returns {Promise}
   */
  validateRelationships(data: any): Promise {
    const header = {
      modifiedRelationships: data
    };
    return fetch(`${ROOT_URL}validateRelationships/`).post(header);
  },

  /**
   * Save Query Source
   *  @param {Object} data
   * @returns {Object}
   */
  saveRelationships(data: any): Promise {
    return fetch(`${ROOT_URL}relationships/`).post(data);
  },

  /**
   * Load indicator
   *  @param {String} tenantId
   * @returns {Promise}
   */
  loadIndicator(tenantId: string): Promise {
    const tenant = isNil(tenantId) ? '' : tenantId;
    return fetch(`${ROOT_URL}indicator/${tenant}`).get();
  },

  /**
   * Delate relationship
   *  @param {String} relationshipId
   * @returns {Promise}
   */
  deleteRelationship(relationshipId: string): Promise {
    return fetch(`${ROOT_URL}relationship/${relationshipId}`).delete();
  },

  /**
   * Delate Query Source 
   *  @param {String} querySourceFieldId
   * @returns {Promise}
   */
  deleteQuerySourceField(querySourceFieldId: string): Promise {
    return fetch(`${ROOT_URL}querySourceField/${querySourceFieldId}`).delete();
  },

  /**
   * Load function/operators
   * @param {String} tenantId
   * @param {String} querySourceId
   * @param {Array} includeCustomJsonFunction
   * @returns {Promise}
   */
  fetchFunctionOperators(tenantId: string, querySourceId: string, includeCustomJsonFunction: any): Promise {
    const data = {
      querySourceIds: isArray(querySourceId) ? querySourceId : [querySourceId],
      tenantId,
      includeCustomJsonFunction
    };
    return fetch(`${ROOT_URL}functionOperators`).post(data);
  },

  /**
   * validate expression in data source
   * @param {String} querySourceId
   * @param {String} expression
   * @param {String} izendaDataType
   * @param {String} querySourceFieldId
   */
  validateExpression(querySourceId: string, expression: string, izendaDataType: string, querySourceFieldId: string): Promise {
    const data = {
      querySourceId,
      expression,
      izendaDataType,
      querySourceFieldId,
      tenantId: uiState.tenantId
    };

    return fetch(`${FUSION_URL}validateDataModelExpression`).post(data);
  },

  /**
   * Preview result of calculated field
   * @param {String} querySourceId
   * @param {String} expression
   * @param {String} izendaDataType
   * @param {String} querySourceFieldId
   * @returns {String} preview result
   */
  previewCalculatedField(querySourceId: string, expression: string, izendaDataType: string, querySourceFieldId: string): Promise {
    const data = {
      querySourceId,
      expression,
      izendaDataType,
      querySourceFieldId,
      tenantId: uiState.tenantId
    };

    return fetch(`${FUSION_URL}calculatedFieldValue`).post(data);
  },

  /**
   * Load database name for custom view popup
   * @param {Object} data
   * @returns {Promise}
   */
  getDatabases(data: any): Promise {
    return fetch(`${ROOT_URL}basicConnectionsInfo`).post(data);
  },

  /**
   * Load database name for import/database mapping
   * @param {Object} requestData
   * @returns {Promise}
   */
  getConnectionInfo(requestData: any): Promise {
    return fetch(`${ROOT_URL}connectionsInfo`).post(requestData);
  },

  /**
   * Load schema name for custom view popup
   * @param {Object} data
   * @returns {Promise}
   */
  getSchemas(data: any): Promise {
    return fetch(`${ROOT_URL}basicQuerySourceCategoriesInfo`).post(data);
  },

  /**
   * Validation for custom view popup
   * @param {Object} data
   * @returns {Promise}
   */
  validateCustomQuerySource(data: any): Promise {
    return fetch(`${ROOT_URL}validateCustomQuerySource`).post(data);
  },

  /**
   * Save custom view
   * @param {Object} data
   * @returns {Promise}
   */
  saveCustomQuerySource(data: any): Promise {
    return fetch(`${ROOT_URL}customQuerySource`).post(data);
  },

  /**
   * Delete custom query source
   * @param {Object} data
   * @return {Promise}
   */
  deleteCustomQuerySource(data: any): Promise {
    return fetch(`${ROOT_URL}deleteCustomQuerySource`).post(data);
  }
};



// WEBPACK FOOTER //
// ./ui/fetchers/Izenda.DataModelDataFetcher.js